import RowsCard from "@/components/company/shared/rows-card";
import SectionHeaderWithIcon from "@/components/company/shared/section-header-with-icon";
import { useTranslation } from "@/hooks/useTranslation";
import {
  factKey,
  formatFactDate,
  type CompanyWebFactSection,
  type CompanyWebFactSectionId,
  type LinkedCompanyWebFact,
} from "@/lib/company-web-facts";
import { cn } from "@/lib/utils";
import {
  BriefcaseIcon,
  CoinsIcon,
  InfoIcon,
  NewspaperIcon,
  ScaleIcon,
  StarIcon,
  UserPlusIcon,
  type LucideIcon,
} from "lucide-react";

const SECTION_ICONS: Record<CompanyWebFactSectionId, LucideIcon> = {
  activity: BriefcaseIcon,
  financials: CoinsIcon,
  press: NewspaperIcon,
  hiring: UserPlusIcon,
  reviews: StarIcon,
  legal: ScaleIcon,
  other: InfoIcon,
};

/**
 * One sourced statement: the text, then its source host and date. The whole
 * row links to the page, which makes a comfortable touch target. While the
 * profile streams, it blurs in as it is written.
 */
export function CompanyWebFactRow({
  fact,
  isWriting = false,
}: {
  fact: LinkedCompanyWebFact;
  isWriting?: boolean;
}) {
  const { currentLanguage } = useTranslation();
  const date = formatFactDate(fact.date, currentLanguage);
  return (
    <a
      href={fact.source.href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "-mx-3 flex min-w-0 flex-col gap-0.5 px-3 py-2.5 outline-none transition-colors hover:bg-accent/50 focus-visible:bg-accent/50 max-sm:min-h-11",
        isWriting && "blur-in-appear",
      )}
    >
      <span className="break-words text-sm leading-snug">{fact.text}</span>
      <span className="truncate text-xs text-muted-foreground">
        {[fact.source.host, date].filter(Boolean).join(" · ")}
      </span>
    </a>
  );
}

/**
 * One section per category that has facts, in contract order. While the
 * profile streams, a section and each row blur in as they are written.
 */
export default function CompanyWebFactSections({
  sections,
  isWriting = false,
}: {
  sections: CompanyWebFactSection[];
  isWriting?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <>
      {sections.map((section) => (
        <section key={section.id} className={cn(isWriting && "blur-in-appear")}>
          <SectionHeaderWithIcon
            icon={SECTION_ICONS[section.id]}
            title={t(`company.webPreview.sections.${section.id}`)}
          />
          <RowsCard>
            {section.facts.map((fact) => (
              <CompanyWebFactRow
                key={factKey(fact)}
                fact={fact}
                isWriting={isWriting}
              />
            ))}
          </RowsCard>
        </section>
      ))}
    </>
  );
}
